import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Building2, Users, Eye, Lightbulb, Compass, Shield, ArrowRight, Award } from 'lucide-react';

const AboutUs = () => {
  const values = [
    {
      icon: Eye,
      title: 'Transparency',
      description: 'We believe in clear, honest communication at every stage. No hidden fees, no jargon — just straightforward guidance you can trust.',
    },
    {
      icon: Users,
      title: 'Client-First Approach',
      description: 'Your goals drive everything we do. We take the time to understand your unique situation and tailor solutions that work for you.',
    },
    {
      icon: Compass,
      title: 'Expert Guidance',
      description: 'Our award-winning brokers bring deep industry knowledge and strong lender relationships to secure the best outcomes for our clients.',
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We embrace technology and fresh thinking to streamline the lending process, making it faster, simpler, and more accessible for all Australians.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About Us"
        description="Award-winning mortgage brokers with offices in Darwin, Melbourne, Brisbane and Sydney. Expert lending guidance for Australians."
        path="/about"
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#00594C] to-[#003d35] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <Building2 className="w-8 h-8 text-[#99DF63]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">About Manage Your Loans</h1>
            <p className="text-lg text-gray-200">
              Award-winning mortgage brokers empowering Australians with expert lending guidance
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-[#00594C] mb-6">Our Story</h2>
            <div className="space-y-6">
              <p className="text-lg text-gray-600 leading-relaxed">
                Manage Your Loans was founded to simplify the lending experience for Australians. We understand that navigating the world of home loans, refinancing, and investment lending can be overwhelming — and we set out to change that.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                As award-winning mortgage brokers with offices in <strong>Darwin</strong>, <strong>Melbourne</strong>, <strong>Brisbane</strong> and <strong>Sydney</strong>, we bring local expertise with national reach. We are proudly part of the <strong>Interetail group</strong>, combining innovative thinking with deep industry experience.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our mission is to empower clients with insights throughout their loan journey — from the first conversation through to settlement and beyond. We don't just find you a loan; we help you understand it, manage it, and make it work for your future.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#00594C] mb-4">Our Values</h2>
              <p className="text-gray-600 text-lg">The principles that guide everything we do</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {values.map((value) => (
                <div
                  key={value.title}
                  className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="w-12 h-12 bg-[#99DF63]/20 rounded-lg flex items-center justify-center mb-4">
                    <value.icon className="w-6 h-6 text-[#00594C]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#00594C] mb-2">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#00594C] mb-4">Meet Our Team</h2>
              <p className="text-gray-600 text-lg">Award-winning brokers across Australia</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8 border border-gray-100">
              <p className="text-gray-600 leading-relaxed text-lg">
                Manage Your Loans is led by <strong>Caitlyn</strong>, alongside brokers <strong>Kevin</strong>, <strong>Chantelle</strong>, and <strong>Kate</strong> — each bringing years of hands-on experience in the Australian home lending market. Supported by a skilled admin and executive team, we're built to guide you from first enquiry to settlement with care and expertise. With offices in Darwin, Melbourne, Brisbane and Sydney, we're here when you need us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Accreditations */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#00594C] mb-4">Accreditations</h2>
            </div>
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-6 border border-gray-100 flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#99DF63]/20 rounded-full flex items-center justify-center">
                  <Award className="w-5 h-5 text-[#00594C]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#00594C] mb-1">MFAA Member</h3>
                  <p className="text-gray-600 text-sm">Proud member of the Mortgage & Finance Association of Australia</p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-100 flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#99DF63]/20 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#00594C]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#00594C] mb-1">Credit Representative 540988</h3>
                  <p className="text-gray-600 text-sm">
                    Credit Representative of Home Mortgage Plus Pty Ltd ACN 105 991 839 (Australian Credit Licence 392200)
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-100 flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#99DF63]/20 rounded-full flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#00594C]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#00594C] mb-1">ABN 34 652 442 790</h3>
                  <p className="text-gray-600 text-sm">Manage Your Loans Pty Ltd</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-[#00594C] mb-4">Ready to Get Started?</h2>
            <p className="text-gray-600 text-lg mb-8">
              Book a consultation with one of our award-winning brokers today.
            </p>
            <a
              href="/#book-appointment"
              className="inline-flex items-center px-8 py-4 bg-[#00594C] text-white font-semibold rounded-full hover:bg-[#003d35] transition-colors duration-300"
            >
              Book an Appointment
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
